import React, { useEffect, useState } from "react"
import moment from "moment"
import { Sparkles, X } from "lucide-react"

import Button from "../../../components/ui/button/button"

import { ResultDisplay } from "./result-display"
import { runParse } from "./run-parse"

/**
 * Modal for running the parser against a single queued enquiry, displaying
 * the result, and saving it back onto the queue document.
 *
 * Pulls the active parser configuration from localStorage (whatever the
 * Development tab last persisted), so iterating on the prompt and re-parsing
 * a queued enquiry just means: tweak the prompt on the Development tab,
 * switch back to the Enquiry Queue tab, hit Parse on the row.
 *
 * The full settings snapshot is written alongside the result so we can later
 * tell which prompt/model produced a saved parse — that's the feedback loop.
 */
function ParseWindow({
	entry,
	close,
	persist = true,
	initialResult = null,
	initialSettings = null,
	onParsed,
}) {
	// Seed visible result from either:
	//   - `initialResult` / `initialSettings` (transient mode — website tab)
	//   - the entry's saved Firestore fields (persisted mode — email tab)
	// If the user re-parses, this gets replaced with the fresh result.
	const [running, setRunning] = useState(false)
	const [error, setError] = useState(null)
	const [savedResult, setSavedResult] = useState(() => {
		if (initialResult) return initialResult
		if (entry.last_parse_result) {
			return {
				output_text: "",
				parsed: entry.last_parse_result,
				model_used: entry.last_parse_model || null,
				usage: entry.last_parse_usage || null,
			}
		}
		return null
	})
	const [activeSettings, setActiveSettings] = useState(() => {
		if (initialSettings) return initialSettings
		if (entry.last_parse_settings) {
			return {
				fields: entry.last_parse_settings.fields || [],
				predictions: entry.last_parse_settings.predictions || [],
			}
		}
		return null
	})

	const lastParsedAt = entry.last_parse_at?.seconds
		? moment.unix(entry.last_parse_at.seconds).format("D MMM YYYY HH:mm")
		: null

	const run = async () => {
		setRunning(true)
		setError(null)

		try {
			const { data, settings } = await runParse(entry, { persist })
			const nextResult = {
				output_text: data.output_text || "",
				parsed: data.parsed || null,
				model_used: data.model_used || settings.model,
				usage: data.usage || null,
			}
			const nextSettings = {
				fields: settings.fields,
				predictions: settings.predictions,
			}
			setSavedResult(nextResult)
			setActiveSettings(nextSettings)
			// Push back up so transient callers (website tab) can keep their
			// per-row cache in sync after an in-modal re-parse.
			onParsed?.({
				result: nextResult,
				settings: nextSettings,
				parsedAt: Date.now(),
			})
		} catch (err) {
			setError(err?.message || String(err))
		} finally {
			setRunning(false)
		}
	}

	// Lock background scroll while the modal is open — keeps the user from
	// accidentally scrolling the page underneath when they're trying to scroll
	// inside the modal body.
	useEffect(() => {
		const previous = document.body.style.overflow
		document.body.style.overflow = "hidden"
		return () => {
			document.body.style.overflow = previous
		}
	}, [])



	const subtitle = entry.from_name
		? `${entry.from_name} <${entry.from_address || ""}>`
		: entry.from_address || ""

	// Click-on-backdrop closes the modal; clicks on the card itself shouldn't
	// bubble up to do the same. Owning the structure (rather than going
	// through the shared <Window>) lets us apply a clean flex-column +
	// internal-scroll layout without fighting the base window.scss rules.
	return (
		<div
			className="enquiry-parse-overlay"
			role="dialog"
			aria-modal="true"
			onClick={close}>
			<div
				className="enquiry-parse-card"
				onClick={(e) => e.stopPropagation()}>
				<div className="enquiry-parse-header">
					<div className="enquiry-parse-header-text">
						<h1>{entry.subject || "(no subject)"}</h1>
						{subtitle && <small>{subtitle}</small>}
					</div>
					<button
						type="button"
						className="enquiry-parse-close"
						onClick={close}
						aria-label="Close">
						<X size={18} />
					</button>
				</div>

				<div className="enquiry-parse-body">
					<div className="enquiry-parse-window-meta">
						{lastParsedAt && (
							<small>
								<strong>Last parsed:</strong> {lastParsedAt}
							</small>
						)}
						{entry.comm_id && (
							<small>
								<strong>Source comm:</strong> {entry.comm_id}
							</small>
						)}
					</div>

					<div className="enquiry-parse-window-actions">
						<Button
							small={true}
							label={savedResult ? "Reparse" : "Parse"}
							icon={<Sparkles size={14} />}
							loading={running}
							loadingText="Parsing..."
							onClick={run}
							disabled={running || (!entry.comm_id && !entry.body)}
						/>
					</div>

					{error && (
						<div
							className="ai-panel-error"
							role="alert">
							{error}
						</div>
					)}

					{savedResult && activeSettings && (
						<ResultDisplay
							result={savedResult}
							fields={activeSettings.fields}
							predictions={activeSettings.predictions}
						/>
					)}

					{!savedResult && !error && (
						<p className="enquiry-parse-window-empty">No parse run yet.</p>
					)}
				</div>
			</div>
		</div>
	)
}

export default ParseWindow
